// src/components/EditPanel.jsx
import React, { useEffect, useRef, useState } from "react";
import { getActiveEditId, setActiveEditId } from "../state/activeEditStore";
import { addToEdit, removeEditItem, getEditItems, clearEdit, deleteEditItems } from "../state/editStore";
import { createEditMeta, renameEditMeta, listEdits, getEditMeta, deleteEditMeta } from "../state/editsStore";
import { clearEditFeatures } from "../state/editFeaturesStore";
import { getEditImageSummary } from "../state/editFeatureAggregation";
import { ensureEditAggregateFeatures } from "../features/editFeatureAggregation";
import { getEditAggregate } from "../state/editAggregateStore";
import { EDIT_CHANGED_EVENT } from "../state/editEvents";
import {
  logProductSaved,
  logProductUnsaved,
  logPromptSubmitted,
} from "../lib/interactionEventsApi";
import { getShopperToken } from "../lib/api";
import { saveEditItems, saveEditMeta } from "../lib/editApi";

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}


async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function EditPanel({
  open,
  onClose,
  onRequestSnapshot, // if provided: can return a dataUrl or call back with one
}) {
  const fileInputRef = useRef(null);

  const [noteDraft, setNoteDraft] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);

  const [activeEditId, setActiveEditIdState] = useState(() => getActiveEditId() || "");
  const [boardItems, setBoardItems] = useState([]);
  const [summaryTick, setSummaryTick] = useState(0);
  const [editAgg, setEditAgg] = useState(null);

  const [editName, setEditName] = useState("");
const [saveState, setSaveState] = useState("saved"); // "saving" | "saved"
const saveTimerRef = useRef(null);

const [toast, setToast] = useState(null);
const toastTimerRef = useRef(null);

function showToast(message, tone = "success") {
  setToast({ message, tone });

  if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

  toastTimerRef.current = setTimeout(() => {
    setToast(null);
  }, 2600);
}

  const [allEdits, setAllEdits] = useState([]);

  function ensureActiveEdit() {
    let id = getActiveEditId();
    if (!id) {
      // Create a proper Edit meta entry so it appears in your registry
      const meta = createEditMeta({ name: "" });
      id = meta.id;
      setActiveEditId(id);
    }
    setActiveEditIdState(id);
    return id;
  }

  function startNewEdit() {
    const meta = createEditMeta({ name: "" });
    setActiveEditId(meta.id);
    setActiveEditIdState(meta.id);
    setAllEdits(listEdits());
    // clear local view (store will be empty because it's a new id)
    setBoardItems([]);
  }

  // Load items for the active edit whenever panel opens / edit changes
  useEffect(() => {
  if (!open) return;
  const id = ensureActiveEdit();
  setAllEdits(listEdits());
  setEditName(getEditMeta(id)?.name || "");
  const items = getEditItems(id);
  setBoardItems(items);
  
  // Kick aggregate compute (async)
  (async () => {
    const agg = await ensureEditAggregateFeatures(id, items);
    setEditAgg(agg || getEditAggregate(id));
  })();
}, [open]);

 useEffect(() => {
  if (!open) return;
  if (!activeEditId) return;

  const items = getEditItems(activeEditId);
  setBoardItems(items);
  setEditName(getEditMeta(activeEditId)?.name || "");

  (async () => {
    const agg = await ensureEditAggregateFeatures(activeEditId, items);
    setEditAgg(agg || getEditAggregate(activeEditId));
  })();
}, [activeEditId, open]);

useEffect(() => {
  if (!open) return;

  const onChanged = (ev) => {
    // emitEditChanged(editId) usually sends CustomEvent with detail.editId
    const changedId =
      ev?.detail?.editId || ev?.detail || ev?.editId || null;

    // only refresh if it’s the edit we’re currently showing
    if (!changedId || changedId !== activeEditId) return;

    const items = getEditItems(activeEditId);
    setBoardItems(items);

    (async () => {
      const agg = await ensureEditAggregateFeatures(activeEditId, items);
      setEditAgg(agg || getEditAggregate(activeEditId));
    })();
  };

  window.addEventListener(EDIT_CHANGED_EVENT, onChanged);
  return () => window.removeEventListener(EDIT_CHANGED_EVENT, onChanged);
}, [open, activeEditId]);

function onChangeEditName(next) {
  setEditName(next);
  if (!activeEditId) return;

  setSaveState("saving");

  if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

  saveTimerRef.current = setTimeout(() => {
    try {
      renameEditMeta(activeEditId, next.trim());
    } finally {
      setSaveState("saved");
    }
  }, 450);
}

  const addItem = (item) => {
  const id = ensureActiveEdit();
  const next = { id: uid(), createdAt: Date.now(), ...item };
  addToEdit(next, id);
    const isProduct = next.kind === "pinned_product";

if (isProduct && next.productId != null) {
  logProductSaved({
    productId: next.productId,
    editId: id,
    surface: "edit_panel",
    category: next.category || null,
    variant: next.variant || null,
  });
}
const items = getEditItems(id);
setBoardItems(items);

(async () => {
  const agg = await ensureEditAggregateFeatures(id, items);
  setEditAgg(agg || getEditAggregate(id));
})();
  setSummaryTick((x) => x + 1);
};

  const removeItem = (itemId) => {
  if (!activeEditId) return;

  const before = getEditItems(activeEditId) || [];
  const target = before.find((x) => x.id === itemId) || null;

  removeEditItem(itemId, activeEditId);
const items = getEditItems(activeEditId);
setBoardItems(items);

const isProduct = target?.kind === "pinned_product";

if (isProduct && target?.productId != null) {
  logProductUnsaved({
    productId: target.productId,
    editId: activeEditId,
    surface: "edit_panel",
    category: target.category || null,
    variant: target.variant || null,
  });
}

(async () => {
  const agg = await ensureEditAggregateFeatures(activeEditId, items);
  setEditAgg(agg || getEditAggregate(activeEditId));
})();
  setSummaryTick((x) => x + 1);
};

 const clearAll = () => {
  if (!activeEditId) return;

  const items = getEditItems(activeEditId) || [];

  clearEdit(activeEditId);
clearEditFeatures(activeEditId);
setEditAgg(null);
setBoardItems([]);
  setSummaryTick((x) => x + 1);
};

const handleSaveEdit = async () => {
  if (!activeEditId) return;

 if (!getShopperToken()) {
  window.dispatchEvent(
    new CustomEvent("veya_auth_required", {
      detail: {
        reason: "save_edit",
        message: "To save this edit, create an account or log in.",
        editId: activeEditId,
      },
    })
  );
  return;
}

  try {
    const meta = getEditMeta(activeEditId);
    const items = getEditItems(activeEditId) || [];

    await saveEditMeta({
      id: activeEditId,
      name: (meta?.name || "").trim() || "Untitled edit",
      templateSlug: meta?.templateSlug || null,
    });

    await saveEditItems(activeEditId, items);

showToast("Edit has been saved.", "success");
  } catch (err) {
    console.error("[edit] save failed", err);
    showToast("Could not save this edit right now.", "error");
  }
};

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    for (const f of files) {
      const dataUrl = await fileToDataUrl(f); // ✅ persistent
      addItem({
        kind: "image",
        imageUrl: dataUrl,
        title: f.name?.replace(/\.[^/.]+$/, "") || "Image",
        sourceUrl: null,
      });
    }
  };

  const onPaste = async (e) => {
    const clipItems = e.clipboardData?.items;
    if (!clipItems) return;

    for (const it of clipItems) {
      if (it.type && it.type.startsWith("image/")) {
        const blob = it.getAsFile();
        if (!blob) continue;
        const dataUrl = await fileToDataUrl(blob); // ✅ persistent
        addItem({
          kind: "image",
          imageUrl: dataUrl,
          title: "Screenshot",
          sourceUrl: null,
        });
      }
    }
  };

  const hasPins = (boardItems?.length || 0) > 0;
  const summary = activeEditId ? getEditImageSummary(activeEditId) : null;

  if (!open) return null;

  return (
    <div
      className="side-panel right-panel"
      onPaste={onPaste}
      tabIndex={-1}
      style={{
        background: "linear-gradient(180deg, rgba(91, 31, 43, 0.92), rgba(91, 31, 43, 0.85))",
        border: "1px solid rgba(176, 140, 108, 0.22)",
        boxShadow: "0 18px 50px rgba(0,0,0,0.40)",
      }}
    >
      <div
        className="side-panel-header"
        style={{
          background: "transparent",
          borderBottom: "1px solid rgba(251, 247, 242, 0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
         <input
  value={editName}
  onChange={(e) => onChangeEditName(e.target.value)}
  placeholder="Give this edit a name"
  style={{
    width: "100%",
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    color: "rgba(251, 247, 242, 0.98)",
    fontSize: 28,
    fontWeight: 800,
    outline: "none",
  }}
/>
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: "rgba(251, 247, 242, 0.78)",
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
           <span style={{ opacity: 0.85, fontSize: 12 }}>
  {saveState === "saving" ? "Saving…" : "Saved"}
</span>
 
            {editAgg?.palette?.dominant_families?.length ? (
  <span style={{ opacity: 0.9 }}>
    Palette:{" "}
    <span style={{ fontWeight: 700, color: "rgba(251, 247, 242, 0.92)" }}>
      {editAgg.palette.dominant_families.join(", ")}
    </span>
  </span>
) : null}

            <button
              type="button"
              className="ghost-btn"
              onClick={startNewEdit}
              style={{
                borderRadius: 12,
                padding: "6px 10px",
                background: "rgba(251, 247, 242, 0.22)",
                border: "1px solid rgba(251, 247, 242, 0.22)",
                color: "rgba(251, 247, 242, 0.92)",
                fontWeight: 700,
              }}
              title="Start a new Edit"
            >
              New Edit
            </button>

            <select
  value={activeEditId}
  onChange={(e) => {
    const nextId = e.target.value;
    setActiveEditId(nextId);
    setActiveEditIdState(nextId);
    setEditName(getEditMeta(nextId)?.name || "");
  }}
  style={{
    borderRadius: 12,
    padding: "6px 10px",
    background: "rgba(251, 247, 242, 0.18)",
    border: "1px solid rgba(251, 247, 242, 0.18)",
    color: "rgba(251, 247, 242, 0.92)",
    fontWeight: 700,
    colorScheme: "dark",
  }}
  title="Switch edit"
>
  {allEdits.map((m) => (
<option
  key={m.id}
  value={m.id}
  style={{
    background: "#5b1f2b",
    color: "#fbf7f2",
    fontWeight: 700,
  }}
>
  {(m.name || "").trim() || "Untitled edit"}
</option>
  ))}
</select>

            <button
              className="ghost-btn"
              onClick={() => (window.location.href = "/edits")}
              style={{
                borderRadius: 12,
                padding: "6px 10px",
                background: "rgba(251, 247, 242, 0.18)",
                border: "1px solid rgba(251, 247, 242, 0.18)",
                color: "rgba(251, 247, 242, 0.92)",
                fontWeight: 700,
              }}
              title="View all edits"
            >
              View all edits
            </button>
          </div>
          {summary && (
  <div style={{ width: "100%", marginTop: 6 }}>
    <div style={{ fontSize: 12, opacity: 0.85 }}>
      Palette:{" "}
      <span style={{ fontWeight: 800 }}>
        {summary.palette.families.join(" · ")}
      </span>
      {summary.palette.strategy ? ` (${summary.palette.strategy})` : ""}
    </div>

    {summary.garmentSubtypes?.length > 0 && (
      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
        Garments:{" "}
        <span style={{ fontWeight: 700 }}>
          {summary.garmentSubtypes.join(" · ")}
        </span>
      </div>
    )}
  </div>
)}
        </div>

        <button
          className="panel-close"
          onClick={onClose}
          aria-label="Close Edit"
          style={{
            background: "rgba(251, 247, 242, 0.72)",
            border: "1px solid rgba(176, 140, 108, 0.25)",
            flex: "0 0 auto",
          }}
        >
          ✕
        </button>
      </div>

      <div className="side-panel-body" style={{ background: "transparent" }}>
        <div className="panel-section" style={{ marginBottom: 12 }}>
          <p
            className="field-help"
            style={{
              marginTop: 0,
              color: "rgba(251, 247, 242, 0.85)",
            }}
          >
            Save pieces, screenshots, and details you’re drawn to.
            <br />
            <span style={{ opacity: 0.65, fontSize: 12 }}>
              Veya uses your saved products + your notes + your screenshots to rank recommendations.
            </span>
            <br />
            <span style={{ opacity: 0.65, fontSize: 12 }}>
              Tip: paste a screenshot here (Ctrl+V).
            </span>
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="ghost-btn"
              onClick={() => fileInputRef.current?.click()}
              style={{
                borderRadius: 14,
                background: "rgba(251, 247, 242, 0.92)",
                border: "1px solid rgba(176, 140, 108, 0.30)",
                fontWeight: 700,
              }}
            >
              Add image
            </button>

            <button
              className="ghost-btn"
              onClick={async () => {
                if (!onRequestSnapshot) {
                  alert("Snapshot wiring next: connect to the Canvas and return a dataUrl.");
                  return;
                }

                // Support both patterns:
                // 1) onRequestSnapshot() returns a dataUrl
                // 2) onRequestSnapshot(cb) calls cb(dataUrl)
                try {
                  const maybe = await onRequestSnapshot((dataUrl) => {
                    if (dataUrl) {
                      addItem({ kind: "image", imageUrl: dataUrl, title: "Snapshot", sourceUrl: null });
                    }
                  });

                  if (typeof maybe === "string" && maybe.startsWith("data:image/")) {
                    addItem({ kind: "image", imageUrl: maybe, title: "Snapshot", sourceUrl: null });
                  }
                } catch (e) {
                  console.error("[edit] snapshot failed", e);
                }
              }}
              style={{
                borderRadius: 14,
                background: "rgba(251, 247, 242, 0.80)",
                border: "1px solid rgba(176, 140, 108, 0.25)",
              }}
              title="Save a snapshot of your mannequin here"
            >
              Quick photo
            </button>

            <button
              className="ghost-btn"
              onClick={() => setNoteOpen((v) => !v)}
              style={{
                borderRadius: 14,
                background: "rgba(251, 247, 242, 0.86)",
                border: "1px solid rgba(176, 140, 108, 0.26)",
              }}
              title="Add a note"
            >
              Add note
            </button>

            {hasPins && (
              <button
                className="ghost-btn"
                onClick={clearAll}
                style={{
                  borderRadius: 14,
                  background: "rgba(251, 247, 242, 0.72)",
                  border: "1px solid rgba(176, 140, 108, 0.22)",
                }}
                title="Remove all pins"
              >
                Clear
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {noteOpen && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 16,
                background: "rgba(251, 247, 242, 0.92)",
                border: "1px solid rgba(176, 140, 108, 0.22)",
                boxShadow: "0 10px 22px rgba(0,0,0,0.12)",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  opacity: 0.75,
                  marginBottom: 8,
                }}
              >
                Add a note
              </div>

              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="e.g. deep burgundy, clean lines, no busy prints, soft drape…"
                rows={2}
                style={{
                  width: "100%",
                  resize: "vertical",
                  borderRadius: 12,
                  padding: 10,
                  border: "1px solid rgba(43,43,43,0.10)",
                  background: "rgba(255,255,255,0.75)",
                  fontFamily: "inherit",
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: "rgba(43,43,43,0.90)",
                  outline: "none",
                }}
              />

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
                <button
                  className="ghost-btn"
                  onClick={() => {
                    setNoteOpen(false);
                    setNoteDraft("");
                  }}
                  style={{
                    borderRadius: 12,
                    background: "rgba(251, 247, 242, 0.70)",
                    border: "1px solid rgba(176, 140, 108, 0.18)",
                  }}
                >
                  Cancel
                </button>

                <button
                  className="ghost-btn"
                  onClick={() => {
  const text = (noteDraft || "").trim();
  if (!text) return;

  const editId = ensureActiveEdit();

  addItem({
    kind: "note",
    title: "Note",
    text,
    sourceUrl: null,
  });

  setNoteDraft("");
  setNoteOpen(false);
}}
                  style={{
                    borderRadius: 12,
                    background: "rgba(251, 247, 242, 0.92)",
                    border: "1px solid rgba(176, 140, 108, 0.30)",
                    fontWeight: 800,
                  }}
                >
                  Save note
                </button>
              </div>
            </div>
          )}
        </div>

        {!hasPins ? (
          <div className="panel-section">
            <p className="field-help" style={{ color: "rgba(251, 247, 242, 0.78)" }}>
              Your Edit is empty.
              <br />
              Add products from Browse, paste screenshots, and write notes — those are all used for recommendations.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 14,
              paddingBottom: 6,
            }}
          >
            {boardItems.map((pin) => {
              const tilt = (((pin.createdAt % 9) - 4) * 0.6).toFixed(2);
              const lift = pin.createdAt % 2 ? 6 : 0;

              if (pin.kind === "note") {
                return (
                  <div
                    key={pin.id}
                    style={{
                      background: "rgba(251, 247, 242, 0.92)",
                      border: "1px solid rgba(176, 140, 108, 0.18)",
                      borderRadius: 16,
                      padding: 12,
                      boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
                      transform: `rotate(${tilt}deg)`,
                      marginTop: lift,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: 12,
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        background: "rgba(176, 140, 108, 0.95)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                      }}
                      aria-hidden="true"
                    />

                    <button
                      className="remove-btn"
                      onClick={() => removeItem(pin.id)}
                      title="Remove note"
                      aria-label="Remove note"
                      style={{ width: 26, height: 26, borderRadius: 10 }}
                    >
                      ✕
                    </button>

                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 12,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        opacity: 0.65,
                        marginBottom: 6,
                      }}
                    >
                      {pin.title || "Note"}
                    </div>

                    <div style={{ fontSize: 14, lineHeight: 1.4, color: "rgba(43,43,43,0.88)" }}>
                      {pin.text || ""}
                    </div>
                  </div>
                );
              }

                           if (pin.kind === "pinned_product" || pin.kind === "image") {
                const isProduct = pin.kind === "pinned_product";
                const label = isProduct ? "Product" : "Image";
                const fallbackTitle = isProduct ? `Product #${pin.productId}` : "Saved";

                return (
                  <div
                    key={pin.id}
                    style={{
                      background: "rgba(251, 247, 242, 0.96)",
                      border: "1px solid rgba(43, 43, 43, 0.10)",
                      borderRadius: 16,
                      padding: 10,
                      boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
                      transform: `rotate(${tilt}deg)`,
                      marginTop: lift,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: 12,
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        background: "rgba(176, 140, 108, 0.95)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                      }}
                      aria-hidden="true"
                    />

                    {/* type badge */}
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 44,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        padding: "4px 8px",
                        borderRadius: 999,
                        background: "rgba(0,0,0,0.05)",
                        border: "1px solid rgba(0,0,0,0.08)",
                        color: "rgba(43,43,43,0.80)",
                      }}
                      title={label}
                    >
                      {label}
                    </div>

                    <button
                      className="remove-btn"
                      onClick={() => removeItem(pin.id)}
                      title="Remove pin"
                      aria-label="Remove pin"
                      style={{ width: 26, height: 26, borderRadius: 10 }}
                    >
                      ✕
                    </button>

                    <div
                      style={{
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "1px solid rgba(43, 43, 43, 0.10)",
                        background: "rgba(251, 247, 242, 0.60)",
                      }}
                    >
                      {pin.imageUrl ? (
                        <img
                          src={pin.imageUrl}
                          alt={pin.title || (isProduct ? "Saved product" : "Saved image")}
                          style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }}
                        />
                      ) : (
                        <div style={{ height: 160, background: "rgba(0,0,0,0.05)" }} />
                      )}
                    </div>

                    <div style={{ marginTop: 8 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "rgba(43,43,43,0.92)",
                        }}
                        title={pin.title || ""}
                      >
                        {pin.title || fallbackTitle}
                      </div>

                      {pin.sourceUrl ? (
                        <a
                          href={pin.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "inline-block",
                            marginTop: 4,
                            fontSize: 12,
                            color: "rgba(43,43,43,0.90)",
                            opacity: 0.85,
                            textDecoration: "underline",
                          }}
                          title="Open item"
                        >
                          View item
                        </a>
                      ) : (
                        <div style={{ marginTop: 4, fontSize: 12, opacity: 0.65 }}>
                          {isProduct ? "Added from catalogue" : "Added by you"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              // kind === "image" is handled above now
              return null;
            })}
          </div>
        )}
      </div>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            right: 28,
            bottom: 28,
            zIndex: 9999,
            maxWidth: 320,
            padding: "14px 16px",
            borderRadius: 18,
            background:
              toast.tone === "error"
                ? "linear-gradient(135deg, rgba(91, 31, 43, 0.98), rgba(70, 22, 34, 0.98))"
                : "linear-gradient(135deg, rgba(91, 31, 43, 0.98), rgba(126, 62, 76, 0.98))",
            border: "1px solid rgba(176, 140, 108, 0.42)",
            boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
            color: "rgba(251, 247, 242, 0.98)",
            fontWeight: 800,
            fontSize: 14,
            letterSpacing: "0.01em",
          }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: "rgba(232, 204, 180, 0.92)",
              marginBottom: 4,
            }}
          >
            Veya
          </div>
          {toast.message}
        </div>
      )}
      
          <div
        className="side-panel-footer"
        style={{
          background: "transparent",
          borderTop: "1px solid rgba(251, 247, 242, 0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <button
          className="ghost-btn"
          onClick={onClose}
          style={{
            borderRadius: 14,
            background: "rgba(251, 247, 242, 0.90)",
            border: "1px solid rgba(176, 140, 108, 0.30)",
          }}
        >
          Close
        </button>

        <button
          className="ghost-btn"
          onClick={handleSaveEdit}
          style={{
            borderRadius: 14,
            background: "rgba(251, 247, 242, 0.98)",
            border: "1px solid rgba(176, 140, 108, 0.34)",
            fontWeight: 800,
          }}
          title="Save this edit to your account"
        >
          Save edit
        </button>
      </div>
    </div>
  );
}